import { RefObject, useEffect } from "react";

interface UseIntersectionObserverProps {
  /**
   * The target element to observe
   */
  target: RefObject<Element | null>;

  /**
   * Callback function to be triggered when intersection occurs
   */
  onIntersect?: () => void;

  /**
   * The threshold value between 0 and 1
   * @default 0.1
   */
  threshold?: number;

  /**
   * Margin around the root element
   * @default "50px"
   */
  rootMargin?: string;

  /**
   * Whether the observer should be enabled
   * @default true
   */
  enabled?: boolean;
}

export const useIntersectionObserver = ({
  target,
  onIntersect,
  threshold = 0.1,
  rootMargin = "50px",
  enabled = true,
}: UseIntersectionObserverProps): void => {
  useEffect(() => {
    if (!enabled || !target?.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersect?.();
          }
        });
      },
      {
        threshold,
        rootMargin,
      },
    );

    const element = target.current;
    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [target, enabled, onIntersect, threshold, rootMargin]);
};
